/**
 * Search input component with Dashboard3 styling
 *
 * @example
 * ```tsx
 * <Search
 *   value={search}
 *   placeholder="Search classes..."
 *   onChange={(e) => setSearch(e.target.value)}
 * />
 * ```
 */

"use client";

import { SearchIcon } from "@chakra-ui/icons";
import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface SearchProps {
  value?: string;
  variant?: string;
  background?: string;
  placeholder?: string;
  borderRadius?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /** Attached to the text field (Playwright selector target). */
  "data-testid"?: string;
  [x: string]: any;
}

export function Search({
  value,
  onChange,
  onKeyDown,
  background,
  placeholder,
  borderRadius,
  variant = "auth",
  "data-testid": dataTestId,
  ...rest
}: SearchProps) {
  const theme = useThemeTokens();

  return (
    <InputGroup h="44px" w="100%" minW="240px" {...rest}>
      <InputLeftElement h="100%">
        <IconButton
          size="sm"
          bg="transparent"
          aria-label="search"
          borderRadius="inherit"
          sx={{
            "@media (hover: hover)": {
              "&:hover": {
                bg: "transparent",
              },
            },
            "@media (hover: none) and (pointer: coarse)": {
              "&:active": {
                bg: "transparent",
              },
            },
          }}
          _focus={{ boxShadow: "none" }}
          icon={<SearchIcon color={theme.text.secondary} w="16px" h="16px" />}
        />
      </InputLeftElement>

      <Input
        h="44px"
        pl="42px"
        minW={0}
        fontSize="sm"
        value={value}
        data-testid={dataTestId}
        fontWeight="500"
        variant={variant}
        color={theme.text.primary}
        onChange={onChange}
        onKeyDown={onKeyDown}
        borderRadius={borderRadius || "8px"}
        placeholder={placeholder || "Search..."}
        _placeholder={{ color: theme.text.secondary, fontSize: "14px" }}
        bg="transparent"
        borderColor={theme.borderGlass.default}
        _focus={{
          borderColor: theme.accent.primaryBorder,
          boxShadow: `0 0 0 1px ${theme.accent.primaryBorder}`,
          bg: "transparent !important",
        }}
        _hover={{
          bg: "transparent !important",
        }}
        _active={{
          bg: "transparent !important",
        }}
        sx={{
          "&[data-active]": {
            bg: "transparent !important",
          },
        }}
      />
    </InputGroup>
  );
}
